import { isDefined } from "@clipboard-health/util-ts";
import { apiClient } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/apiTypes";
import {
  type IncludedWorkplaceSchema,
  type ShiftData,
  type ShiftListQuery,
  type ShiftsListResponse,
  shiftsListResponseSchema,
} from "@src/appV2/Shifts/api/useGetShifts";
import { useQueries } from "@tanstack/react-query";
import { addDays, subDays } from "date-fns";
import { stringify } from "qs";

const MAX_PAST_SHIFTS = 5;
const MAX_FUTURE_SHIFTS = 5;
const PAST_DAYS_RANGE = 3;
const FUTURE_DAYS_RANGE = 7;

export interface WorkerShift {
  id: string;
  qualificationName: string | undefined;
  startAt: string | undefined;
  endAt: string | undefined;
  workplaceName: string | undefined;
  workplaceTimezone: string | undefined;
  workplaceId: string;
  hasClockIn: boolean;
  hasClockOut: boolean;
}

export interface UseGetWorkerShiftsResult {
  shifts: WorkerShift[];
  isLoading: boolean;
  hasShifts: boolean;
}

interface UseGetWorkerShiftsParams {
  workerId: string | undefined;
  enabled?: boolean;
}

function getWorkplaceFromIncluded(
  workplaceId: string,
  included: ShiftsListResponse["included"]
): IncludedWorkplaceSchema | undefined {
  if (!isDefined(included)) {
    return undefined;
  }

  return included.find(
    (item): item is IncludedWorkplaceSchema =>
      item.type === API_TYPES.workplace && item.id === workplaceId
  );
}

function mapShiftToWorkerShift(
  shift: ShiftData,
  included: ShiftsListResponse["included"]
): WorkerShift {
  const workplaceId = shift.relationships?.workplace?.data?.id ?? "";
  const workplace = getWorkplaceFromIncluded(workplaceId, included);

  return {
    id: shift.id,
    qualificationName: shift.attributes?.requirements?.qualificationName,
    startAt: shift.attributes?.schedule?.startAt,
    endAt: shift.attributes?.schedule?.endAt,
    workplaceName: workplace?.attributes?.name,
    workplaceTimezone: workplace?.attributes?.timezone,
    workplaceId,
    hasClockIn: isDefined(shift.attributes?.clockInOut?.start),
    hasClockOut: isDefined(shift.attributes?.clockInOut?.end),
  };
}

function buildShiftQuery(params: {
  workerId: string;
  startGt?: Date;
  startLt?: Date;
  sortOrder: "asc" | "desc";
  pageSize: number;
}): ShiftListQuery {
  const { workerId, startGt, startLt, sortOrder, pageSize } = params;

  return {
    filter: {
      agentId: workerId,
      start: {
        ...(isDefined(startGt) && { gt: startGt }),
        ...(isDefined(startLt) && { lt: startLt }),
      },
    },
    fields: {
      shift: [
        "schedule.startAt",
        "schedule.endAt",
        "requirements.qualificationName",
        "clockInOut.start",
        "clockInOut.end",
      ],
      workplace: ["name", "timezone"],
    },
    include: [API_TYPES.workplace],
    sort: {
      field: "schedule.startAt",
      order: sortOrder,
    },
    page: {
      size: pageSize,
    },
  };
}

async function fetchShifts(query: ShiftListQuery): Promise<ShiftsListResponse> {
  const response = await apiClient.get<ShiftsListResponse>("/v3/shifts", {
    params: query,
    paramsSerializer: (params) => stringify(params, { arrayFormat: "comma" }),
  });

  return shiftsListResponseSchema.parse(response.data);
}

export function useGetWorkerShifts(params: UseGetWorkerShiftsParams): UseGetWorkerShiftsResult {
  const { workerId, enabled = true } = params;

  const isQueryEnabled = enabled && isDefined(workerId);

  const results = useQueries({
    queries: [
      {
        queryKey: ["workerShifts", "past", workerId],
        queryFn: async () => {
          const now = new Date();
          const pastStartDate = subDays(now, PAST_DAYS_RANGE);
          const query = buildShiftQuery({
            workerId: workerId ?? "",
            startGt: pastStartDate,
            startLt: now,
            sortOrder: "desc",
            pageSize: MAX_PAST_SHIFTS,
          });
          return await fetchShifts(query);
        },
        enabled: isQueryEnabled,
        staleTime: 30_000,
      },
      {
        queryKey: ["workerShifts", "future", workerId],
        queryFn: async () => {
          const now = new Date();
          const futureEndDate = addDays(now, FUTURE_DAYS_RANGE);
          const query = buildShiftQuery({
            workerId: workerId ?? "",
            startGt: now,
            startLt: futureEndDate,
            sortOrder: "asc",
            pageSize: MAX_FUTURE_SHIFTS,
          });
          return await fetchShifts(query);
        },
        enabled: isQueryEnabled,
        staleTime: 30_000,
      },
    ],
  });

  const [pastResult, futureResult] = results;
  const isLoading = pastResult.isLoading || futureResult.isLoading;

  const shifts: WorkerShift[] = [];

  if (isDefined(pastResult.data)) {
    const { data: pastShiftsData, included: pastIncluded } = pastResult.data;
    const pastShifts = pastShiftsData
      .slice(0, MAX_PAST_SHIFTS)
      .map((shift) => mapShiftToWorkerShift(shift, pastIncluded))
      .reverse();
    shifts.push(...pastShifts);
  }

  if (isDefined(futureResult.data)) {
    const { data: futureShiftsData, included: futureIncluded } = futureResult.data;
    const futureShifts = futureShiftsData
      .slice(0, MAX_FUTURE_SHIFTS)
      .map((shift) => mapShiftToWorkerShift(shift, futureIncluded));
    shifts.push(...futureShifts);
  }

  return {
    shifts,
    isLoading,
    hasShifts: shifts.length > 0,
  };
}
